import { inject, Injectable } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";

import { ExtendedHierarchyNode, MetaTags } from "#src/core/backend/controllers";

import { CanonicalUrlService } from "./canonical-url.service";


const brand = "Retrodigital";
const siteTagline = "Historische Unterlagen St.Gallen";
const titleSeparator = " | ";

const defaultMeta = {
  tags: {
    keywords: [
      //
      "St.Gallen",
      "Retrodigital",
      "Publikationen",
      "Staatskanzlei",
      "Datenbank",
      "St.Galler Gemeinden",
      // Gemini suggested keywords:
      "Kantonsgeschichte St.Gallen",
      "Kulturerbe digital",
      "Genealogie St.Gallen",
      "Quellenforschung",
      "Staatsarchiv St.Gallen",
      "Kantonsarchiv",
      "Stiftsarchiv",
      "Kantonale Verwaltung SG",
    ].join(","),
    title: siteTagline,
    description:
      "Die Retrodigital-Plattform ist ein Angebot des Kantons St.Gallen. Sie bietet die Möglichkeit, auf verschiedene Publikationen, die früher ausschliesslich gedruckt erschienen sind, orts- und zeitunabhängig online zugreifen zu können.",
  } satisfies MetaTags,
  postfix: {} satisfies Partial<MetaTags>,
};

@Injectable({ providedIn: "root" })
export class MetaTagService {
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly canonicalUrl = inject(CanonicalUrlService);

  resetTags() {
    this.updateTags(defaultMeta);
  }

  /**
   * Keyword first, brand last: search engines weight the leading words of a title most heavily.
   * Segments equal to the brand are dropped so it can only appear once, at the end — several
   * callers pass a node title that already matches it.
   */
  private composeTitle(tags: Partial<MetaTags>, postfix: Partial<MetaTags>): string {
    const segments = [tags.title, postfix.title].map((segment) => segment?.trim()).filter((segment): segment is string => !!segment);
    const unique = segments.filter(
      (segment, index) =>
        segment.toLowerCase() !== brand.toLowerCase() &&
        segments.findIndex((other) => other.toLowerCase() === segment.toLowerCase()) === index
    );
    return [...unique, brand].join(titleSeparator);
  }

  updateTags({ tags, postfix = defaultMeta.postfix }: { tags: Partial<MetaTags>; postfix?: Partial<MetaTags> }) {
    const title = this.composeTitle(tags, postfix);
    const description = `${tags.description ?? defaultMeta.tags.description}${postfix?.description ?? ""}`;
    const url = this.canonicalUrl.toAbsolute(location.pathname);

    this.title.setTitle(title);
    this.meta.updateTag({ name: "title", content: title });
    this.meta.updateTag({ name: "keywords", content: `${tags.keywords ?? defaultMeta.tags.keywords}${postfix?.keywords ?? ""}` });
    this.meta.updateTag({ name: "description", content: description });

    // og:image is intentionally absent: no asset of the required ~1200x630 exists yet.
    this.meta.updateTag({ property: "og:title", content: title });
    this.meta.updateTag({ property: "og:description", content: description });
    this.meta.updateTag({ property: "og:url", content: url });
    this.meta.updateTag({ property: "og:type", content: "website" });
    this.meta.updateTag({ property: "og:site_name", content: brand });
    this.meta.updateTag({ property: "og:locale", content: "de_CH" });
    this.meta.updateTag({ name: "twitter:card", content: "summary" });
  }

  updateTagsUsingNode({
    node,
    fallback,
    postfix,
  }: {
    node: ExtendedHierarchyNode | null;
    fallback?: Partial<MetaTags>;
    postfix?: Partial<MetaTags>;
  }) {
    this.updateTags({
      tags: {
        title: node?.metaTags?.title || (node?.title ?? fallback?.title),
        keywords: node?.metaTags?.keywords || (node?.children?.map((child) => child.title).join(",") ?? fallback?.keywords),
        description: node?.metaTags?.description || fallback?.description,
      },
      postfix,
    });
  }
}
